import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogTitle, IconButton } from "@mui/material";
import { useRef } from "react";

import type { AgentThreadToolUseContent } from "@/shared/domain/agentThread/AgentThreadRecord";
import type { ClientTool } from "@/shared/domain/agentThread/clientTools";
import { Button } from "@/shared/foundation/primitives";

import styles from "./ToolApprovalDetailsModal.module.css";

interface ToolApprovalDetailsModalProps {
  open: boolean;
  onClose: () => void;
  toolUse: AgentThreadToolUseContent;
  tool: ClientTool;
  onApprove: () => void;
  onDecline: () => void;
}

/**
 * Detail modal for previewing what a client-side tool will do.
 *
 * Opened from the inline ToolApprovalCard via the "Details" button.
 * Shows the tool's ConfirmationContent (or a JSON fallback) along with
 * Approve/Decline actions so the user can act without closing the modal.
 *
 * Approve/Decline are deferred until after the Dialog exit animation
 * completes to avoid unmounting the parent ToolApprovalCard (and this
 * Dialog) mid-transition.
 */
export function ToolApprovalDetailsModal({
  open,
  onClose,
  toolUse,
  tool,
  onApprove,
  onDecline,
}: ToolApprovalDetailsModalProps) {
  const CustomContent = tool.ConfirmationContent;
  const params = toolUse.input ?? {};

  // Store the action to execute after the Dialog exit animation finishes.
  const pendingActionRef = useRef<(() => void) | null>(null);

  const handleApprove = () => {
    pendingActionRef.current = onApprove;
    onClose();
  };

  const handleDecline = () => {
    pendingActionRef.current = onDecline;
    onClose();
  };

  const handleExited = () => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: handleExited }}
    >
      <DialogTitle className={styles.dialogTitle}>
        <span className={styles.titleText}>{tool.displayName}</span>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <div className={styles.content}>
        {CustomContent ? (
          <CustomContent params={params} />
        ) : (
          <pre className={styles.jsonPreview}>
            {JSON.stringify(params, null, 2)}
          </pre>
        )}
      </div>

      <div className={styles.actions}>
        <Button variant="outlined" onClick={handleDecline} size="small">
          Decline
        </Button>
        <Button variant="contained" onClick={handleApprove} size="small">
          Approve
        </Button>
      </div>
    </Dialog>
  );
}
